"use client";

import type { LeadFormSurface } from "@homeinstead/analytics";
import type { Dispatch, SetStateAction } from "react";
import type { CareSeekerLeadFormResponse } from "~www/types/forms/care-seeker-shallow-lead-form";
import type { LeadResponse } from "~www/types/forms/lead-response";
import type {
  LeadFlowDocument,
  LeadFlowOfficeSubmission,
  LeadFlowStepErrors,
  LeadFlowSubmission,
} from "../types";

import {
  startTransition,
  useActionState,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { submitCareSeekerShallowForm } from "~www/actions/submitCareSeekerShallowForm";
import { FALLBACK_BOTID_TAGS } from "~www/lib/bot-id/bot-id-tags";
import { isHoneypotRejection } from "~www/utils/is-honeypot-rejection";
import { useLeadFlowAnswerStore } from "../components/LeadFlowAnswerStoreProvider";
import { NO_STEPS } from "../constants/empty-values";
import { createFormData } from "../utils/create-form-data";
import { getErrorStepIndex } from "../utils/get-error-step-index";
import { getStepParameterName } from "../utils/get-step-parameter-name";
import { readStepParameter } from "./use-step-parameter";

type LeadFlowSteps = NonNullable<LeadFlowDocument["steps"]>;
type LeadFlowResponse = LeadResponse<CareSeekerLeadFormResponse>;

type UseLeadFlowSubmissionInput = {
  blockKey: string;
  clearErrors: () => void;
  goToStep: (index: number, options?: { replace?: boolean }) => void;
  leadFlow: LeadFlowDocument;
  /** The URL step parameter as it is now, not as the transition shows it. */
  liveStepParameter: string | null;
  officeSubmission: LeadFlowOfficeSubmission;
  setSubmission: Dispatch<SetStateAction<LeadFlowSubmission | null>>;
  showErrors: (stepId: string, errors: LeadFlowStepErrors) => void;
  /** The URL step parameter the transition is showing. */
  stepParameter: string | null;
  submission: LeadFlowSubmission | null;
};

/**
 * The earliest step asking a field the server rejected, or -1 when the
 * response has no field errors a step can show: a non-validation failure, a
 * honeypot rejection, or errors on fields no step asks (injected office
 * fields, the form's own metadata), which then show as a submission error.
 */
function getResponseErrorStepIndex(
  steps: LeadFlowSteps,
  response: LeadFlowResponse,
): number {
  const { validationErrors } = response;
  if (!validationErrors || isHoneypotRejection(validationErrors)) {
    return -1;
  }

  return getErrorStepIndex(steps, Object.keys(validationErrors));
}

/**
 * Sends the page's answers through the care seeker server action and handles
 * each response once: success clears the page's answers and records the step
 * the thank-you screen replaces; field errors send the visitor to the
 * earliest step with one; anything else shows on the last step.
 */
function useSubmission({
  blockKey,
  clearErrors,
  goToStep,
  leadFlow,
  liveStepParameter,
  officeSubmission,
  setSubmission,
  showErrors,
  stepParameter,
  submission,
}: UseLeadFlowSubmissionInput) {
  const store = useLeadFlowAnswerStore();
  const steps = leadFlow.steps ?? NO_STEPS;
  // Bumped when this flow submits and when another flow clears the shared
  // answers. The submit stamps its epoch on the payload, because a queued
  // action does not start until the previous one finishes and would otherwise
  // read the epoch after a reset. A response publishes only when that stamp
  // is still current.
  const epochRef = useRef(0);
  const epochByFormDataRef = useRef(new WeakMap<FormData, number>());
  const [currentResponse, setCurrentResponse] =
    useState<LeadFlowResponse | null>(null);

  const [, submitAction, isPending] = useActionState<
    LeadFlowResponse | null,
    FormData
  >(async (previousState, formData) => {
    const epoch = epochByFormDataRef.current.get(formData);
    let response: LeadFlowResponse;
    try {
      response = await submitCareSeekerShallowForm(previousState, formData);
    } catch (error) {
      // A network failure or a server crash rejects the action instead of
      // returning a response; it shows as a submission error like any other.
      response = {
        error: error instanceof Error ? error.message : "Submission failed",
        ok: false,
        tags: previousState?.tags ?? FALLBACK_BOTID_TAGS,
      };
    }

    if (epoch === undefined || epoch !== epochRef.current) {
      return response;
    }

    // An `ok` response whose lead API result reports an error (a duplicate
    // or otherwise) still shows the thank-you, matching the legacy forms: a
    // repeat visitor or a bot is not told the lead was refused.
    if (response.ok) {
      // Read the URL as the request settles: the visitor can change steps
      // while it is pending.
      const thankYouStepParameter = readStepParameter(
        getStepParameterName(blockKey),
      );
      setSubmission(
        (current) => current && { ...current, thankYouStepParameter },
      );
    }

    setCurrentResponse(response);
    return response;
  }, null);

  // Leaving the thank-you screen, by browser Back or Forward, ends the
  // submission once the step change has settled: the flow starts again from
  // its cleared answers, and reaching the same step later shows that step
  // rather than the old confirmation.
  if (
    submission?.thankYouStepParameter !== undefined &&
    liveStepParameter !== submission.thankYouStepParameter &&
    stepParameter !== submission.thankYouStepParameter
  ) {
    setSubmission(null);
  }

  // A successful submit clears the shared answers. Every other flow returns
  // to its first step and drops the attempt it was on, including a failed
  // submit or a thank-you. This flow skips that only while it is the one
  // clearing, so its thank-you stays up.
  const isOwnClearRef = useRef(false);
  useEffect(
    () =>
      store.subscribeToClear(() => {
        if (isOwnClearRef.current) {
          return;
        }
        epochRef.current += 1;
        setCurrentResponse(null);
        setSubmission(null);
        clearErrors();
        goToStep(0, { replace: true });
      }),
    [clearErrors, goToStep, setSubmission, store],
  );

  const appliedResponseRef = useRef(currentResponse);
  useEffect(() => {
    if (!currentResponse || appliedResponseRef.current === currentResponse) {
      return;
    }
    appliedResponseRef.current = currentResponse;

    if (currentResponse.ok) {
      isOwnClearRef.current = true;
      store.clear();
      isOwnClearRef.current = false;
      return;
    }

    // Both jumps replace the current entry. A pushed entry would make the
    // in-form Back walk history back to the step the visitor submitted from.
    const errorStepIndex = getResponseErrorStepIndex(steps, currentResponse);
    const errorStep = steps[errorStepIndex];
    if (!errorStep || !currentResponse.validationErrors) {
      // The submission error shows on the last step, and the visitor may
      // have navigated away while the request was pending.
      goToStep(steps.length - 1, { replace: true });
      return;
    }

    const fieldErrors = Object.fromEntries(
      Object.entries(currentResponse.validationErrors).map(
        ([fieldName, message]) => [fieldName, [message]],
      ),
    );
    showErrors(errorStep.id, fieldErrors);
    goToStep(errorStepIndex, { replace: true });
  }, [goToStep, showErrors, currentResponse, steps, store]);

  const submit = useCallback(
    (formElement: HTMLFormElement, surface: LeadFormSurface) => {
      const answers = store.getSnapshot();
      const formData = createFormData({
        answers,
        formElement,
        officeSubmission,
        versionId: leadFlow.versionId,
      });
      epochRef.current += 1;
      epochByFormDataRef.current.set(formData, epochRef.current);
      setCurrentResponse(null);
      setSubmission({ answers, surface });
      startTransition(() => {
        submitAction(formData);
      });
    },
    [leadFlow.versionId, officeSubmission, setSubmission, store, submitAction],
  );

  return {
    /** The current response failed in a way no step's fields can show. */
    hasUnroutableError:
      currentResponse?.ok === false &&
      getResponseErrorStepIndex(steps, currentResponse) < 0,
    isPending,
    state: currentResponse,
    submit,
  };
}

export { useSubmission };
